import { calgaryCalendarDate } from '../../../../../../utils/calgaryTime';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faPlus, faClock, faCircleCheck, faInfoCircle, faLock } from "@fortawesome/free-solid-svg-icons";
import { FiChevronLeft, FiChevronRight, FiHelpCircle, FiInbox, FiLock } from "react-icons/fi";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isWithinInterval, isToday } from "date-fns";
import { useParams } from "react-router-dom";
import FinancialService from "@frontend/AppSettings/services/FinancialService";
import EmployeeService from "@frontend/AppSettings/services/EmployeeService";
import MatterService from "@frontend/AppSettings/services/MatterService";
import RateService from "@frontend/AppSettings/services/RateService";
import MatterCostCodeBudgetService, { CostCodeBudgetMap } from "@frontend/AppSettings/services/MatterCostCodeBudgetService";
import BillingCategoryService from "@frontend/AppSettings/services/BillingCategoryService";
import { BillingCategory } from "@frontend/Models/Rate/RateModels";
import useActiveEmployeeId from "@frontend/AppSettings/hooks/useActiveEmployeeId";
import toastService from "@frontend/AppSettings/services/ToastService";
import LogBillableEntryModal from "../LogBillableEntryModal/LogBillableEntryModal";
import { isFeatureEnabled } from "@frontend/AppSettings/environmentConfig";
import { useDayLocks } from "@frontend/Financial/TimesheetDayLocks/useDayLocks";
import {
  useInvalidateTimeRequests,
  usePendingTimeRequests,
  useWeekTimeRequests,
} from "@frontend/Financial/TimesheetDayLocks/useTimeRequests";
import { TimeRequest } from "@frontend/Financial/TimesheetDayLocks/TimeRequestService";
import DayLockHeaderToggle from "@frontend/Financial/TimesheetDayLocks/DayLockHeaderToggle";
import AnchoredPopover from "@frontend/Financial/TimesheetDayLocks/AnchoredPopover";
import TimeRequestPopover, { TimeRequestCell } from "@frontend/Financial/TimesheetDayLocks/TimeRequestPopover";
import TimeRequestsPanel from "@frontend/Financial/TimesheetDayLocks/TimeRequestsPanel";
import LockedDaysPanel from "@frontend/Financial/TimesheetDayLocks/LockedDaysPanel";
import { DateLock, NewDateLock } from "@frontend/Financial/TimesheetDayLocks/TimesheetDayLockService";
import {
  formatRequestHours,
  isDayLockMessage,
  requestCellKey,
  serverErrorMessage,
} from "@frontend/Financial/TimesheetDayLocks/dayLockHelpers";
import { blockNumberWheel, preventNumberStepKeys } from "@frontend/utils/manualNumberInput";
import "./BillingTimesheetView.css";

const WEEK_STARTS_ON = 1;

/**
 * Turn an HttpService error into an accurate budget message. Only a real 403 is the "admins only"
 * case; a 404 (matter not found), 400 (validation), 500, or network error must surface the server's
 * own message instead of a misleading permissions line (the previous blanket text made a failed
 * write for any reason look like a permission problem).
 */
function budgetErrorMessage(err: any, subject: string): string {
  const status = err?.status;
  const serverMsg = err?.data?.error || err?.data?.message;
  if (status === 403) return serverMsg || `Only administrators can change the ${subject}.`;
  return serverMsg || err?.message || `Failed to update ${subject}.`;
}

// Last-resort fallback ONLY. The cost-code list is data-driven from the billing_categories catalog
// (Phase 2). This constant is used solely when a tenant's catalog is empty (e.g. never seeded), so
// the grid never renders zero category rows. Migration 0028 seeds these same codes for every tenant.
const DEFAULT_TIMESHEET_CATEGORIES = [
  { code: "100", description: "Mechanical Drawing Package Development" },
  { code: "110", description: "Mechanical Model Development" },
  { code: "200", description: "Civil-Structural Engineering & Design" },
  { code: "210", description: "Structural Model Development" },
  { code: "300", description: "Electrical Drawing Package Development" },
  { code: "400", description: "Instrumentation Base Scope" },
  { code: "500", description: "Mechanical/ Project Engineering & Design" },
  { code: "600", description: "Project administration" },
  { code: "900", description: "Field Services" },
] as const;

interface TimeEntry {
  bt_id?: number;
  cw_id?: number;
  fr_id?: number;
  id?: number;
  time_recorded?: string;
  date_recorded?: string;
  date?: string;
  notes?: string;
  matterid?: string;
  employeeid?: string;
  employee_name?: string;
  amount?: number;
  billedrate?: number;
  costofworkrate?: number;
  feildrate?: number;
  invoiceid?: string | null;
}

interface Employee {
  employeeid: string;
  name: string;
  billedrate: number;
  rateId: string | null;
}

interface EmployeeAmounts {
  billedTotal: number;
  costofworkTotal: number;
  fieldrateTotal: number;
}

interface CategoryDayData {
  hours: number;
  notes: string[];
  entryIds: number[];
  invoiced: boolean;
  billed: number;
  costofwork: number;
  fieldrate: number;
}

interface CategoryRow {
  categoryCode: string;
  categoryDescription: string;
  days: Record<string, CategoryDayData>;
  rowTotal: number;
}

interface DayAmounts {
  billed: number;
  costofwork: number;
  fieldrate: number;
}

interface TimesheetRow {
  employeeId: string;
  employeeName: string;
  billedRate: number;
  days: Record<string, number>;
  dayAmounts: Record<string, DayAmounts>;
  rowTotal: number;
  categories: CategoryRow[];
  amounts: EmployeeAmounts;
}

function parseTimeRecordedToHours(timeRecorded: string | undefined): number {
  if (!timeRecorded) return 0;
  const s = String(timeRecorded).trim();
  if (s.includes(":")) {
    const parts = s.split(":");
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    const sec = parseInt(parts[2], 10) || 0;
    return h + m / 60 + sec / 3600;
  }
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function formatHoursToDisplay(hours: number): string {
  if (hours <= 0) return "";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  return `${h}:${m.toString().padStart(2, "0")}`;
}

function formatHoursToHoursMinutes(hours: number): string {
  if (hours <= 0) return "0h 00m";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

function formatTimeForApi(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:00`;
}

function extractCategoryCode(notes: string): string {
  const trimmed = (notes || "").trim();
  const match = trimmed.match(/^(\d{3})/);
  return match ? match[1] : "uncategorized";
}

function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  }
  return new Date(dateStr);
}

function getEmployeeNameFromRaw(emp: any): string {
  if (emp.FirstName && emp.LastName) return `${emp.FirstName} ${emp.LastName}`;
  if (emp.firstname && emp.lastname) return `${emp.firstname} ${emp.lastname}`;
  if (emp.FirstName) return emp.FirstName;
  if (emp.firstname) return emp.firstname;
  return emp.EmployeeID || emp.employeeid || "Unknown";
}

interface BillingTimesheetViewProps {
  onRefresh?: () => void;
  /** @deprecated Use matter budget (total project progress) instead. Kept for backwards compatibility. */
  weeklyBudgetCapHours?: number | null;
  matterId?: string | null;
  employeeId?: string | null;
  isTimesheetOnlyUser?: boolean;
  matterStatus?: string;
}

interface PendingCellEdit {
  employeeId: string;
  categoryCode: string;
  dateKey: string;
  newHours: number;
  oldHours: number;
  entryIds: number[];
  billedRate: number;
}

function makeBtsPendingKey(employeeId: string, categoryCode: string, dateKey: string): string {
  return `${employeeId}::${categoryCode}::${dateKey}`;
}

const LOCKED_STATUSES = ["complete", "on hold"];

function isMatterLocked(status: string | undefined): boolean {
  if (!status) return false;
  const cleaned = status.toLowerCase().trim().replace(/^status:\s*/i, "");
  return LOCKED_STATUSES.includes(cleaned);
}

const BillingTimesheetView: React.FC<BillingTimesheetViewProps> = ({
  onRefresh,
  weeklyBudgetCapHours: _weeklyBudgetCapHours = 40,
  matterId: matterIdProp,
  employeeId: employeeIdProp,
  isTimesheetOnlyUser = false,
  matterStatus,
}) => {
  const params = useParams<{ matterId: string }>();
  const matterId = matterIdProp ?? params.matterId;
  const activeEmployeeId = useActiveEmployeeId();

  const [weekStart, setWeekStart] = useState<Date>(() =>
    startOfWeek(calgaryCalendarDate(), { weekStartsOn: WEEK_STARTS_ON })
  );
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [rateCategoriesMap, setRateCategoriesMap] = useState<Map<string, Set<string>>>(new Map());
  const [matterBudgetHours, setMatterBudgetHours] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [timesheetViewMode, setTimesheetViewMode] = useState<"my" | "all">("my");
  const [hoveredCell, setHoveredCell] = useState<{ rowKey: string; colIndex: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{
    employeeId: string;
    categoryCode: string;
    dateKey: string;
  } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingCellEdit>>(new Map());
  const [currentUserPermishId, setCurrentUserPermishId] = useState<string | number | null>(null);
  // Day locks: true once the caller's employee row has loaded, so a NULL permish (least privilege,
  // decision b) is told apart from "still loading" and gets the lock UI the server enforces.
  const [permishResolved, setPermishResolved] = useState(false);

  // Per-cost-code budgets + server-computed group totals (code -> {budget, group}).
  // group is the WHOLE crew's hours for the code (review H1), so it must come from the
  // server, never from the week-filtered/caller-scoped grid memos.
  const [costCodeBudgets, setCostCodeBudgets] = useState<CostCodeBudgetMap>({});
  // The cost-code catalog (billing_categories). Data-driven since Phase 2: a code added in the
  // admin surface auto-appears here. All codes (active + inactive) are held; the grid shows active
  // ones plus any retired code that still carries hours on this matter.
  const [categories, setCategories] = useState<BillingCategory[]>([]);
  // Per-code budgets are edited in ONE place — a collapsible panel under the progress bar — rather
  // than inline beside every employee's category rows (which repeated the same edit control per
  // person). Drafts are keyed by code so the whole breakdown can be entered and saved in one PUT.
  const [showCodeBudgets, setShowCodeBudgets] = useState(false);
  const [codeBudgetDrafts, setCodeBudgetDrafts] = useState<Record<string, string>>({});
  const [isSavingCodeBudgets, setIsSavingCodeBudgets] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const canEditOthersTimesheets = useMemo(() => {
    const n =
      typeof currentUserPermishId === 'string'
        ? parseInt(currentUserPermishId, 10)
        : currentUserPermishId;
    return n === 1;
  }, [currentUserPermishId]);

  // Only administrators (permish_id 1) may change a project's hour budget.
  const canEditBudget = canEditOthersTimesheets;

  const canEditEmployeeTime = useCallback(
    (employeeId: string) => {
      if (!activeEmployeeId) return false;
      if (employeeId === activeEmployeeId) return true;
      return canEditOthersTimesheets;
    },
    [activeEmployeeId, canEditOthersTimesheets]
  );

  const isLockedForUser = isTimesheetOnlyUser && isMatterLocked(matterStatus);

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: WEEK_STARTS_ON });
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    let d = weekStart;
    while (d <= weekEnd) {
      dates.push(d);
      d = addDays(d, 1);
    }
    return dates;
  }, [weekStart, weekEnd]);

  // --- Timesheet day locks (plans/todo/timesheet-day-locks.md, F2) ---------------------------
  // Same wiring as GeneralTimesheetView, scoped to this project: the Requests panel lists only this
  // matter's requests. Locks never apply to permish 1.
  const dayLocksFeature = isFeatureEnabled("timesheetDayLocks");
  const dayLocks = useDayLocks(dayLocksFeature);
  const isDayLockAdmin = dayLocksFeature && canEditOthersTimesheets;
  const dayLockAppliesToMe =
    dayLocksFeature && permishResolved && !canEditOthersTimesheets && dayLocks.hasLocks;
  const weekRequests = useWeekTimeRequests(
    dayLockAppliesToMe && !!matterId,
    format(weekStart, "yyyy-MM-dd"),
    format(weekEnd, "yyyy-MM-dd"),
    matterId,
  );
  const pendingRequests = usePendingTimeRequests(isDayLockAdmin && !!matterId, matterId);
  const invalidateTimeRequests = useInvalidateTimeRequests();
  const [requestCell, setRequestCell] = useState<(TimeRequestCell & { employeeId: string }) | null>(null);
  const [requestAnchor, setRequestAnchor] = useState<HTMLElement | null>(null);
  const [isRequestsPanelOpen, setIsRequestsPanelOpen] = useState(false);
  const [isLockedDaysPanelOpen, setIsLockedDaysPanelOpen] = useState(false);
  const lastClickedCellRef = useRef<HTMLElement | null>(null);

  // Keep sticky employee row flush under the date header as thead height changes.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || isLoading) return;
    const thead = grid.querySelector("thead");
    if (!thead) return;
    const update = () => {
      grid.style.setProperty(
        "--bts-sticky-head-offset",
        `${thead.getBoundingClientRect().height}px`
      );
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(thead);
    return () => ro.disconnect();
  }, [isLoading, weekDates.length]);

  const fetchData = useCallback(async () => {
    if (!matterId) return;
    try {
      setIsLoading(true);

      const toArray = (v: unknown): any[] =>
        Array.isArray(v) ? v : Array.isArray((v as any)?.data) ? (v as any).data : [];

      const [billedTimeRaw, allEmployeesRaw, ratesRaw, codeBudgetsRaw, categoriesRaw] = await Promise.all([
        FinancialService.getBilledTime(matterId),
        EmployeeService.getEmployees().catch(() => []),
        RateService.getRates().catch(() => []),
        MatterCostCodeBudgetService.getBudgets(matterId).catch(() => ({} as CostCodeBudgetMap)),
        BillingCategoryService.getCategories().catch(() => [] as BillingCategory[]),
      ]);

      setCostCodeBudgets(codeBudgetsRaw);
      setCategories(Array.isArray(categoriesRaw) ? categoriesRaw : []);

      const rCatMap = new Map<string, Set<string>>();
      (Array.isArray(ratesRaw) ? ratesRaw : []).forEach((r: any) => {
        if (r.rate_id && r.assoc_categories) {
          const ids = String(r.assoc_categories).split(',').map((s: string) => s.trim()).filter(Boolean);
          if (ids.length > 0) rCatMap.set(r.rate_id, new Set(ids));
        }
      });
      setRateCategoriesMap(rCatMap);

      const billedTimeData = toArray(billedTimeRaw);

      setEntries(billedTimeData);

      const allEmployeesList = toArray(allEmployeesRaw);

      const employeeMap = new Map<string, Employee>();
      allEmployeesList.forEach((e: any) => {
        const id = e.EmployeeID || e.employeeid;
        const rawRate = e.billedrate;
        const isUuid = typeof rawRate === 'string' && rawRate.includes('-');
        if (id) {
          employeeMap.set(id, {
            employeeid: id,
            name: getEmployeeNameFromRaw(e),
            billedrate: isUuid ? 0 : (rawRate || 0),
            rateId: isUuid ? rawRate : null,
          });
        }
      });

      billedTimeData.forEach((entry: any) => {
        const empId = entry.employeeid;
        if (empId && !employeeMap.has(empId)) {
          employeeMap.set(empId, {
            employeeid: empId,
            name: entry.employee_name || `Employee ${empId}`,
            billedrate: 0,
            rateId: null,
          });
        }
      });

      const finalEmployees: Employee[] = [];
      employeeMap.forEach((emp) => finalEmployees.push(emp));
      finalEmployees.sort((a, b) => a.name.localeCompare(b.name));
      setEmployees(finalEmployees);
    } catch (e) {
      console.error("Error fetching timesheet data:", e);
      setEntries([]);
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pending edits belong to the matter that created them. Clear them if this mounted view is
  // reused for another route so a draft can never be projected or saved against the next matter.
  useEffect(() => {
    setPendingEdits(new Map());
    setIsBatchMode(false);
    setEditingCell(null);
    setEditValue("");
  }, [matterId]);

  useEffect(() => {
    const loadPermish = async () => {
      if (!activeEmployeeId) return;
      try {
        const employeeData = await EmployeeService.getEmployeeById(activeEmployeeId);
        if (employeeData) setPermishResolved(true);
        if (employeeData?.permish_id !== undefined) {
          setCurrentUserPermishId(employeeData.permish_id);
        }
      } catch (e) {
        console.error('Error fetching permission for timesheet:', e);
      }
    };
    loadPermish();
  }, [activeEmployeeId]);

  // Fetch matter budget (hourly budget = total project hours cap) for progress bar
  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    (async () => {
      try {
        const matter = await MatterService.getMatterDetails(matterId);
        if (cancelled) return;
        const raw = matter?.budget;
        if (raw != null && raw !== "") {
          const parsed = parseFloat(String(raw).trim());
          if (!isNaN(parsed) && parsed > 0) {
            setMatterBudgetHours(parsed);
            return;
          }
        }
        setMatterBudgetHours(null);
      } catch {
        if (!cancelled) setMatterBudgetHours(null);
      }
    })();
    return () => { cancelled = true; };
  }, [matterId]);

  // The resolved cost-code list the grid renders. Data-driven from the catalog; falls back to the
  // hardcoded defaults only when the tenant's catalog is empty (so a grid never blanks). A retired
  // (inactive) code is hidden UNLESS it still carries hours on this matter, so historical rows keep
  // their bar/rollup (Phase 2 decision (b): retire, never lose history).
  const timesheetCategories = useMemo<Array<{ code: string; description: string }>>(() => {
    if (!categories || categories.length === 0) {
      return DEFAULT_TIMESHEET_CATEGORIES.map((c) => ({ code: c.code, description: c.description }));
    }
    const codesWithHours = new Set<string>();
    entries.forEach((e) => {
      const code = extractCategoryCode((e.notes || "").trim());
      if (code && code !== "uncategorized") codesWithHours.add(code);
    });
    return categories
      .filter((c) => c.active !== false || codesWithHours.has(c.categoryid))
      .map((c) => ({ code: c.categoryid, description: c.category }));
  }, [categories, entries]);

  const { rows, dailyTotals, weekTotal } = useMemo(() => {
    const dayKeys = weekDates.map((d) => format(d, "yyyy-MM-dd"));
    const emptyCatDay = (): CategoryDayData => ({ hours: 0, notes: [], entryIds: [], invoiced: false, billed: 0, costofwork: 0, fieldrate: 0 });

    const byEmpCategory = new Map<string, Map<string, Record<string, CategoryDayData>>>();

    entries.forEach((entry) => {
      const dateStr = entry.date_recorded || entry.date;
      if (!dateStr) return;
      const d = parseLocalDate(dateStr);
      if (isNaN(d.getTime())) return;
      if (!isWithinInterval(d, { start: weekStart, end: weekEnd })) return;

      const empId = entry.employeeid || "unknown";
      if (employeeIdProp && empId !== employeeIdProp) return;

      const key = format(d, "yyyy-MM-dd");
      const hours = parseTimeRecordedToHours(entry.time_recorded);
      const notes = (entry.notes || "").trim();
      const entryId = (entry as any).bt_id ?? (entry as any).btId ?? entry.id;
      const catCode = extractCategoryCode(notes);

      if (!byEmpCategory.has(empId)) {
        byEmpCategory.set(empId, new Map());
      }
      const empCatData = byEmpCategory.get(empId)!;

      if (!empCatData.has(catCode)) {
        empCatData.set(
          catCode,
          Object.fromEntries(dayKeys.map((k) => [k, emptyCatDay()]))
        );
      }
      const catDays = empCatData.get(catCode)!;
      if (!catDays[key]) catDays[key] = emptyCatDay();
      catDays[key].hours += hours;
      if (notes) catDays[key].notes.push(notes);
      const numId = entryId != null && entryId !== "" ? Number(entryId) : undefined;
      if (numId != null && !Number.isNaN(numId)) catDays[key].entryIds.push(numId);
      if (entry.invoiceid) catDays[key].invoiced = true;

      // Field Services (900) bills at the employee's field rate and is tracked
      // as the green "Field" amount. All other categories use the standard
      // billed rate and are tracked as the blue "Billed" amount.
      const isFieldServices = catCode === "900";
      const billedRate = parseFloat(String(entry.billedrate ?? 0)) || 0;
      const fieldRate = parseFloat(String(entry.feildrate ?? 0)) || 0;
      const cwr = parseFloat(String(entry.costofworkrate ?? 0)) || 0;
      catDays[key].billed += isFieldServices ? 0 : hours * billedRate;
      catDays[key].costofwork += hours * cwr;
      catDays[key].fieldrate += isFieldServices ? hours * fieldRate : 0;
    });

    let employeeList = employees;
    if (employeeIdProp) {
      employeeList = employees.filter((e) => e.employeeid === employeeIdProp);
    }

    const rows: TimesheetRow[] = employeeList.map((emp) => {
      const empId = emp.employeeid;
      const empCatData = byEmpCategory.get(empId) || new Map();

      const allowedCategoryIds = emp.rateId ? rateCategoriesMap.get(emp.rateId) : null;
      const empCategories = allowedCategoryIds && allowedCategoryIds.size > 0
        ? timesheetCategories.filter((cat) => allowedCategoryIds.has(cat.code))
        : timesheetCategories;

      const categoryRows: CategoryRow[] = empCategories.map((cat) => {
        const existing = empCatData.get(cat.code);
        const days: Record<string, CategoryDayData> = {};
        dayKeys.forEach((k) => {
          days[k] = existing?.[k] || emptyCatDay();
        });
        const rowTotal = Object.values(days).reduce((a, c) => a + c.hours, 0);
        return {
          categoryCode: cat.code,
          categoryDescription: cat.description,
          days,
          rowTotal,
        };
      });

      const empDays: Record<string, number> = {};
      const dayAmounts: Record<string, DayAmounts> = {};
      dayKeys.forEach((k) => {
        empDays[k] = 0;
        dayAmounts[k] = { billed: 0, costofwork: 0, fieldrate: 0 };
      });
      categoryRows.forEach((cat) => {
        Object.entries(cat.days).forEach(([date, catDay]) => {
          empDays[date] += catDay.hours;
          dayAmounts[date].billed += catDay.billed;
          dayAmounts[date].costofwork += catDay.costofwork;
          dayAmounts[date].fieldrate += catDay.fieldrate;
        });
      });
      const rowTotal = Object.values(empDays).reduce((a, b) => a + b, 0);
      let billedTotal = 0, costofworkTotal = 0, fieldrateTotal = 0;
      Object.values(dayAmounts).forEach((da) => {
        billedTotal += da.billed;
        costofworkTotal += da.costofwork;
        fieldrateTotal += da.fieldrate;
      });

      return {
        employeeId: empId,
        employeeName: emp.name,
        billedRate: emp.billedrate,
        days: empDays,
        dayAmounts,
        rowTotal,
        categories: categoryRows,
        amounts: { billedTotal, costofworkTotal, fieldrateTotal },
      };
    });

    const dailyTotals: Record<string, number> = {};
    dayKeys.forEach((k) => (dailyTotals[k] = 0));
    rows.forEach((r) => {
      Object.entries(r.days).forEach(([date, hours]) => {
        dailyTotals[date] += hours;
      });
    });
    const weekTotal = Object.values(dailyTotals).reduce((a, b) => a + b, 0);

    return { rows, dailyTotals, weekTotal };
  }, [entries, employees, weekStart, weekEnd, weekDates, employeeIdProp, rateCategoriesMap, timesheetCategories]);

  const displayedRowsBase = useMemo(() => {
    if (timesheetViewMode === "my") {
      return rows.filter((row) => row.employeeId === activeEmployeeId);
    }
    return rows;
  }, [rows, timesheetViewMode, activeEmployeeId]);

  const displayedRows = useMemo(() => {
    if (pendingEdits.size === 0) return displayedRowsBase;
    return displayedRowsBase.map(row => {
      const newDays: Record<string, number> = { ...row.days };
      const newCategories = row.categories.map(cat => {
        const newCatDays: Record<string, CategoryDayData> = {};
        Object.entries(cat.days).forEach(([k, v]) => { newCatDays[k] = { ...v }; });
        Object.keys(newCatDays).forEach(dateKey => {
          const pk = makeBtsPendingKey(row.employeeId, cat.categoryCode, dateKey);
          const pending = pendingEdits.get(pk);
          if (pending) {
            const diff = pending.newHours - newCatDays[dateKey].hours;
            newCatDays[dateKey] = { ...newCatDays[dateKey], hours: pending.newHours };
            newDays[dateKey] = (newDays[dateKey] || 0) + diff;
          }
        });
        const catTotal = Object.values(newCatDays).reduce((a, c) => a + c.hours, 0);
        return { ...cat, days: newCatDays, rowTotal: catTotal };
      });
      const empTotal = Object.values(newDays).reduce((a, b) => a + b, 0);
      return { ...row, days: newDays, categories: newCategories, rowTotal: empTotal };
    });
  }, [displayedRowsBase, pendingEdits]);

  // Total project hours (all time, not just current week) for budget progress
  const totalProjectHours = useMemo(() => {
    return entries.reduce((sum, entry) => sum + parseTimeRecordedToHours(entry.time_recorded), 0);
  }, [entries]);

  // Batch edits have not reached the API yet, but they are already the values shown in the grid.
  // Keep the progress UI on that same projected state by applying each cell's net change to the
  // authoritative persisted totals. A Map entry replaces the prior draft for that cell, so a
  // re-edit is counted once rather than accumulated twice.
  const { pendingProjectDelta, pendingDeltaByCode } = useMemo(() => {
    let projectDelta = 0;
    const byCode = new Map<string, number>();
    pendingEdits.forEach((edit) => {
      const delta = edit.newHours - edit.oldHours;
      projectDelta += delta;
      byCode.set(edit.categoryCode, (byCode.get(edit.categoryCode) || 0) + delta);
    });
    return { pendingProjectDelta: projectDelta, pendingDeltaByCode: byCode };
  }, [pendingEdits]);

  const projectedTotalProjectHours = Math.max(0, totalProjectHours + pendingProjectDelta);
  const projectedCodeGroup = useCallback(
    (categoryCode: string): number =>
      Math.max(0, (costCodeBudgets[categoryCode]?.group || 0) + (pendingDeltaByCode.get(categoryCode) || 0)),
    [costCodeBudgets, pendingDeltaByCode]
  );

  // The overall project budget IS the sum of the per-code budgets (matters.budget is derived and
  // kept in sync server-side by upsert_budgets). The total bar's denominator is that sum. A matter
  // with no per-code budgets yet falls back to the legacy matters.budget until codes are set.
  const sumCodeBudgets = useMemo(
    () => Object.values(costCodeBudgets).reduce((s, b) => s + (b.budget || 0), 0),
    [costCodeBudgets]
  );
  const hasCodeBudgets = sumCodeBudgets > 0;
  // The derived total budget = Σ code budgets (falling back to legacy matters.budget only when no
  // code budgets exist). This is the single number the bar AND the client guards use — NEVER the
  // raw matterBudgetHours, which can lag Σ (e.g. before the 0029 backfill) and would wrongly block
  // logging to codes that plainly have room.
  const overallDenominator = hasCodeBudgets ? sumCodeBudgets : matterBudgetHours;

  // Codes the budget dropdown lets an admin edit: every visible (rate-scoped, active/with-hours)
  // code, PLUS any code that already carries a budget but is no longer visible (e.g. a retired code
  // with no hours). Including the latter is what lets its budget — which still inflates the Σ-codes
  // denominator — actually be seen and cleared, rather than being stranded.
  const budgetEditableCodes = useMemo<Array<{ code: string; description: string }>>(() => {
    const list = timesheetCategories.map((c) => ({ code: c.code, description: c.description }));
    const seen = new Set(list.map((c) => c.code));
    for (const [code, b] of Object.entries(costCodeBudgets)) {
      if ((b.budget || 0) > 0 && !seen.has(code)) {
        const cat = categories.find((c) => String(c.categoryid) === code);
        list.push({ code, description: cat?.category || "" });
        seen.add(code);
      }
    }
    return list;
  }, [timesheetCategories, costCodeBudgets, categories]);

  // The resulting per-code total (group total − this cell's current + new) that a save would
  // produce. group comes from the server map, so it is correct for every persona (review H1).
  // Returns null when the code has no positive budget (uncapped).
  const codeResultingOverBudget = useCallback(
    (categoryCode: string, currentHours: number, newHours: number, pendingKey: string): boolean => {
      const b = costCodeBudgets[categoryCode];
      if (!b || b.budget <= 0) return false;
      const existing = pendingEdits.get(pendingKey);
      const existingDelta = existing ? existing.newHours - existing.oldHours : 0;
      const otherPendingDelta = (pendingDeltaByCode.get(categoryCode) || 0) - existingDelta;
      return b.group + otherPendingDelta - currentHours + newHours > b.budget + 1e-9;
    },
    [costCodeBudgets, pendingEdits, pendingDeltaByCode]
  );

  // A per-cost-code block message that names the code and its cap, so it is NOT confused with the
  // project-total block (which uses the "Total amount of billing is completed" wording). A single
  // full code does not mean the whole project budget is spent.
  const codeBudgetMessage = useCallback(
    (categoryCode: string): string => {
      const b = costCodeBudgets[categoryCode];
      const cap = b && b.budget > 0 ? formatHoursToHoursMinutes(b.budget) : null;
      return cap
        ? `Cost code ${categoryCode} has reached its budget (${cap}). Log to a code with hours left, or raise this code's budget.`
        : `Cost code ${categoryCode} has reached its budget. Log to a code with hours left, or raise its budget.`;
    },
    [costCodeBudgets]
  );

  // Recalculate totals for displayed rows
  const { displayedDailyTotals, displayedWeekTotal } = useMemo(() => {
    const dayKeys = weekDates.map((d) => format(d, "yyyy-MM-dd"));
    const dailyTotals: Record<string, number> = {};
    dayKeys.forEach((k) => (dailyTotals[k] = 0));
    displayedRows.forEach((r) => {
      Object.entries(r.days).forEach(([date, hours]) => {
        dailyTotals[date] += hours;
      });
    });
    const weekTotal = Object.values(dailyTotals).reduce((a, b) => a + b, 0);
    return { displayedDailyTotals: dailyTotals, displayedWeekTotal: weekTotal };
  }, [displayedRows, weekDates]);

  const handlePrevWeek = () => setWeekStart((d) => subWeeks(d, 1));
  const handleNextWeek = () => setWeekStart((d) => addWeeks(d, 1));
  const handleToday = () =>
    setWeekStart(startOfWeek(calgaryCalendarDate(), { weekStartsOn: WEEK_STARTS_ON }));
  const handleAddEntry = () => {
    if (isLockedForUser) {
      toastService.warning("This project is locked. You cannot log time to a completed or on-hold project.");
      return;
    }
    if (overallDenominator != null && overallDenominator > 0 && projectedTotalProjectHours >= overallDenominator) {
      toastService.warning("Total amount of billing is completed. Cannot go over limit.");
      return;
    }
    setShowAddModal(true);
  };
  const handleModalSuccess = () => {
    fetchData();
    onRefresh?.();
  };

  const toggleEmployee = (employeeId: string) => {
    setExpandedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const openTimeRequest = (employeeId: string, categoryCode: string, dateKey: string, currentHours: number) => {
    if (!matterId) return;
    const category = timesheetCategories.find((c) => c.code === categoryCode);
    setRequestCell({
      employeeId,
      matterId,
      matterLabel: matterId,
      costCode: categoryCode,
      costCodeLabel: category?.description ?? "",
      dateKey,
      currentHours,
    });
    setRequestAnchor(lastClickedCellRef.current);
  };

  const closeTimeRequest = useCallback(() => {
    setRequestCell(null);
    setRequestAnchor(null);
  }, []);

  const toggleWeekdayLock = async (weekday: number, lock: boolean) => {
    const next = lock
      ? [...dayLocks.lockedWeekdays, weekday]
      : dayLocks.lockedWeekdays.filter((d) => d !== weekday);
    const result = await dayLocks.setLockedWeekdays(next);
    if (result.cancelledRequests > 0) void invalidateTimeRequests();
    return result;
  };

  // Date locks (plans/todo/timesheet-date-locks.md, F2) — firm-wide, like the weekday locks.
  const lockDate = (lock: NewDateLock) => dayLocks.addDateLock(lock);
  const unlockDate = async (lock: DateLock) => {
    const result = await dayLocks.deleteDateLock(lock.lockId);
    if (result.cancelledRequests > 0) void invalidateTimeRequests();
    return result;
  };

  const showRequestInGrid = (request: TimeRequest) => {
    setWeekStart(startOfWeek(parseLocalDate(request.workDate), { weekStartsOn: WEEK_STARTS_ON }));
    setTimesheetViewMode("all");
    setExpandedEmployees((prev) => new Set(prev).add(request.employeeId));
  };

  const handleRequestApproved = async () => {
    await fetchData();
    onRefresh?.();
  };

  const handleCellClick = (
    employeeId: string,
    categoryCode: string,
    dateKey: string,
    currentHours: number,
    isInvoiced: boolean
  ) => {
    if (editingCell || isSaving) return;
    if (!canEditEmployeeTime(employeeId)) return;
    if (isLockedForUser) {
      toastService.warning("This project is locked. You cannot log time to a completed or on-hold project.");
      return;
    }
    if (isInvoiced) {
      toastService.warning("This entry has been invoiced and cannot be edited.");
      return;
    }
    // Day lock (F2): a locked day never enters editing (so never reaches pendingEdits / Save All).
    if (dayLockAppliesToMe && dayLocks.isDayLocked(dateKey)) {
      openTimeRequest(employeeId, categoryCode, dateKey, currentHours);
      return;
    }
    const pendingKey = makeBtsPendingKey(employeeId, categoryCode, dateKey);
    const hasPendingValue = pendingEdits.has(pendingKey);
    if (overallDenominator != null && overallDenominator > 0 && projectedTotalProjectHours >= overallDenominator && currentHours === 0 && !hasPendingValue) {
      toastService.warning("Total amount of billing is completed. Cannot go over limit.");
      return;
    }
    // Per-code advisory: block opening an empty cell on a code already at/over its cap. The
    // server wall is the authority; this just mirrors it so the over code is visibly blocked.
    const cb = costCodeBudgets[categoryCode];
    if (cb && cb.budget > 0 && projectedCodeGroup(categoryCode) >= cb.budget && currentHours === 0 && !hasPendingValue) {
      toastService.warning(codeBudgetMessage(categoryCode));
      return;
    }
    setEditingCell({ employeeId, categoryCode, dateKey });
    setEditValue(currentHours > 0 ? currentHours.toString() : "");
  };

  const handleCellSave = async () => {
    if (!editingCell || isSaving || !matterId) return;
    const newHours = parseFloat(editValue) || 0;

    const row = rows.find((r) => r.employeeId === editingCell.employeeId);
    const categoryRow = row?.categories.find((c) => c.categoryCode === editingCell.categoryCode);
    const entryIds: number[] = categoryRow?.days[editingCell.dateKey]?.entryIds ?? [];
    const currentHours = categoryRow?.days[editingCell.dateKey]?.hours ?? 0;
    const billedRate = row?.billedRate ?? 0;
    const pendingKey = makeBtsPendingKey(editingCell.employeeId, editingCell.categoryCode, editingCell.dateKey);
    const existingPending = pendingEdits.get(pendingKey);
    const existingPendingDelta = existingPending
      ? existingPending.newHours - existingPending.oldHours
      : 0;
    const candidateDelta = newHours - currentHours;

    // Cap on the RESULTING total so reducing/deleting is always allowed and only a
    // net increase that pushes the matter strictly over budget is blocked. Reaching
    // the budget exactly is allowed. (Batch mode aggregates the check on save-all.)
    if (
      !isBatchMode &&
      newHours > 0 &&
      overallDenominator != null &&
      overallDenominator > 0 &&
      totalProjectHours + pendingProjectDelta - existingPendingDelta + candidateDelta > overallDenominator + 1e-9
    ) {
      toastService.warning("Total amount of billing is completed. Cannot go over limit.");
      setEditingCell(null);
      setEditValue("");
      return;
    }

    // Per-code advisory cap (mirrors the server per-cost-code wall). Same "reducing always
    // passes, reaching exactly is allowed" semantics as the matter-total check above.
    if (
      !isBatchMode &&
      newHours > 0 &&
      codeResultingOverBudget(editingCell.categoryCode, currentHours, newHours, pendingKey)
    ) {
      toastService.warning(codeBudgetMessage(editingCell.categoryCode));
      setEditingCell(null);
      setEditValue("");
      return;
    }

    const category = timesheetCategories.find(
      (c) => c.code === editingCell.categoryCode
    );
    const notesValue = category
      ? `${category.code} - ${category.description}`
      : "Billable time";

    const buildUpdatePayload = (hours: number) => {
      const amountNum = hours * billedRate;
      const amount = Number.isFinite(amountNum) ? Number(amountNum.toFixed(2)) : 0;
      return {
        time_recorded: formatTimeForApi(hours),
        date_recorded: editingCell.dateKey,
        notes: notesValue,
        matterid: String(matterId),
        employeeid: String(editingCell.employeeId),
        amount: String(amount),
        tax_percent: "0",
      };
    };

    if (newHours <= 0 && entryIds.length === 0) {
      if (isBatchMode) {
        setPendingEdits(prev => { const next = new Map(prev); next.delete(pendingKey); return next; });
      }
      setEditingCell(null);
      setEditValue("");
      return;
    }

    if (newHours === currentHours) {
      if (isBatchMode) {
        setPendingEdits(prev => {
          const next = new Map(prev);
          next.delete(pendingKey);
          return next;
        });
      }
      setEditingCell(null);
      setEditValue("");
      return;
    }

    // Batch mode validates the projected result before accepting the draft. Exact-cap entries are
    // allowed; only a value that would go over the project or cost-code budget is rejected.
    if (isBatchMode && newHours > 0) {
      if (
        overallDenominator != null &&
        overallDenominator > 0 &&
        totalProjectHours + pendingProjectDelta - existingPendingDelta + candidateDelta > overallDenominator + 1e-9
      ) {
        toastService.warning("Total amount of billing is completed. Cannot go over limit.");
        setEditingCell(null);
        setEditValue("");
        return;
      }
      if (codeResultingOverBudget(editingCell.categoryCode, currentHours, newHours, pendingKey)) {
        toastService.warning(codeBudgetMessage(editingCell.categoryCode));
        setEditingCell(null);
        setEditValue("");
        return;
      }
    }

    if (isBatchMode) {
      setPendingEdits(prev => new Map(prev).set(pendingKey, {
        employeeId: editingCell.employeeId,
        categoryCode: editingCell.categoryCode,
        dateKey: editingCell.dateKey,
        newHours,
        oldHours: currentHours,
        entryIds,
        billedRate,
      }));
      setEditingCell(null);
      setEditValue("");
      return;
    }

    setIsSaving(true);
    try {
      if (entryIds.length > 0) {
        if (newHours <= 0) {
          // Cleared to 0: remove the record(s) instead of storing a 0-hour entry
          for (const id of entryIds) {
            await FinancialService.deleteBilledTime(String(id));
          }
          toastService.success("Time entry deleted");
        } else {
          // Edit existing: set cell to the new value (replace, don't add). Delete the sibling
          // rows FIRST, then update the survivor — otherwise the not-yet-deleted siblings still
          // count toward the per-code sum, so reducing a multi-row cell below its cap could be
          // wrongly refused by the per-code wall (which excludes only the row being updated).
          for (let i = 1; i < entryIds.length; i++) {
            await FinancialService.deleteBilledTime(String(entryIds[i]));
          }
          await FinancialService.updateBilledTime(String(entryIds[0]), buildUpdatePayload(newHours));
          toastService.success("Time entry updated");
        }
      } else {
        // New cell: create one entry
        await FinancialService.logTime({
          time_recorded: formatTimeForApi(newHours),
          date_recorded: editingCell.dateKey,
          notes: notesValue,
          employeeid: editingCell.employeeId,
          matterid: matterId,
        });
        toastService.success("Time entry saved");
      }
      await fetchData();
      onRefresh?.();
    } catch (e) {
      console.error("Error saving time entry:", e);
      // F3: the server's own sentence (budget, project lock, day lock) when it sent one.
      const serverMessage = serverErrorMessage(e);
      if (isDayLockMessage(serverMessage)) dayLocks.refresh();
      toastService.error(serverMessage || "Failed to save time entry");
    } finally {
      setIsSaving(false);
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    preventNumberStepKeys(e);
    if (e.key === "Enter") {
      e.preventDefault();
      handleCellSave();
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleSaveAllPending = async () => {
    if (pendingEdits.size === 0 || !matterId) return;
    const editsToSave: PendingCellEdit[] = [];
    pendingEdits.forEach((edit) => { editsToSave.push(edit); });
    // Apply reductions/deletes before increases so the backend's per-entry cap never
    // trips on a transient over-budget total for a batch whose NET change is within budget.
    editsToSave.sort((a, b) => (a.newHours - a.oldHours) - (b.newHours - b.oldHours));

    // Aggregate cap: the whole batch's net change can't push the matter over budget.
    if (overallDenominator != null && overallDenominator > 0) {
      const netChange = editsToSave.reduce((sum, e) => sum + (e.newHours - e.oldHours), 0);
      if (totalProjectHours + netChange > overallDenominator + 1e-9) {
        toastService.warning("Total amount of billing is completed. Cannot go over limit.");
        return;
      }
    }

    // Per-code aggregate advisory: no single code's group total + its batch net change may
    // exceed that code's budget (mirrors the server per-code wall applied per entry).
    const perCodeNet = new Map<string, number>();
    editsToSave.forEach((e) =>
      perCodeNet.set(e.categoryCode, (perCodeNet.get(e.categoryCode) || 0) + (e.newHours - e.oldHours))
    );
    for (const [code, net] of perCodeNet) {
      const b = costCodeBudgets[code];
      if (b && b.budget > 0 && b.group + net > b.budget + 1e-9) {
        toastService.warning(codeBudgetMessage(code));
        return;
      }
    }

    setIsSaving(true);
    let successCount = 0;
    let failCount = 0;
    let firstFailure: string | null = null;
    try {
      for (let idx = 0; idx < editsToSave.length; idx++) {
        const { employeeId, categoryCode, dateKey, newHours, entryIds, billedRate } = editsToSave[idx];
        const category = timesheetCategories.find((c) => c.code === categoryCode);
        const notesValue = category ? `${category.code} - ${category.description}` : "Billable time";
        const buildPayload = (hours: number) => {
          const amountNum = hours * billedRate;
          const amount = Number.isFinite(amountNum) ? Number(amountNum.toFixed(2)) : 0;
          return {
            time_recorded: formatTimeForApi(hours),
            date_recorded: dateKey,
            notes: notesValue,
            matterid: String(matterId),
            employeeid: String(employeeId),
            amount: String(amount),
            tax_percent: "0",
          };
        };
        try {
          if (entryIds.length > 0) {
            if (newHours <= 0) {
              for (const id of entryIds) {
                await FinancialService.deleteBilledTime(String(id));
              }
            } else {
              // Delete siblings before updating the survivor so the per-code wall's sum doesn't
              // still include soon-to-be-deleted rows (M4).
              for (let i = 1; i < entryIds.length; i++) {
                await FinancialService.deleteBilledTime(String(entryIds[i]));
              }
              await FinancialService.updateBilledTime(String(entryIds[0]), buildPayload(newHours));
            }
          } else {
            await FinancialService.logTime({
              time_recorded: formatTimeForApi(newHours),
              date_recorded: dateKey,
              notes: notesValue,
              employeeid: employeeId,
              matterid: matterId,
            });
          }
          successCount++;
        } catch (e) {
          console.error("Error saving pending edit:", e);
          failCount++;
          if (firstFailure === null) firstFailure = serverErrorMessage(e);
        }
      }
      if (failCount === 0) {
        toastService.success(`Saved ${successCount} time ${successCount === 1 ? "entry" : "entries"}`);
      } else {
        // F3: the first failure's server sentence after the count; behaviour otherwise unchanged.
        if (isDayLockMessage(firstFailure)) dayLocks.refresh();
        toastService.warning(
          firstFailure
            ? `Saved ${successCount}, failed ${failCount}: ${firstFailure}`
            : `Saved ${successCount}, failed ${failCount}`
        );
      }
      setPendingEdits(new Map());
      setIsBatchMode(false);
      await fetchData();
      onRefresh?.();
    } catch (e) {
      console.error("Error in batch save:", e);
      toastService.error("Failed to save time entries");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardPending = () => {
    setPendingEdits(new Map());
    setIsBatchMode(false);
  };

  // Open the panel with drafts seeded from the current budgets so existing values are shown (and are
  // not accidentally cleared) and only the codes the admin actually changes get a new number.
  const openCodeBudgets = () => {
    const init: Record<string, string> = {};
    for (const c of budgetEditableCodes) {
      const b = costCodeBudgets[c.code];
      init[c.code] = b && b.budget > 0 ? String(b.budget) : "";
    }
    setCodeBudgetDrafts(init);
    setShowCodeBudgets(true);
  };

  const handleSaveAllCodeBudgets = async () => {
    if (!matterId || isSavingCodeBudgets) return;
    // Send only the codes whose value actually CHANGED: a new/edited number, or a previously-set
    // budget cleared to blank (→ 0). Untouched codes are skipped (upsert only touches provided rows).
    // A blank/invalid entry is treated as 0 (uncapped), matching the PUT contract.
    const rows: Array<{ cost_code: string; budget_hours: number }> = [];
    for (const c of budgetEditableCodes) {
      const raw = (codeBudgetDrafts[c.code] ?? "").trim();
      const current = costCodeBudgets[c.code]?.budget ?? 0;
      const parsed = raw === "" ? 0 : parseFloat(raw);
      if (raw !== "" && (Number.isNaN(parsed) || parsed < 0)) {
        toastService.warning(`Enter a valid budget in hours (0 or more) for code ${c.code}.`);
        return;
      }
      const next = raw === "" ? 0 : parsed;
      if (next === current) continue; // unchanged
      rows.push({ cost_code: c.code, budget_hours: next });
    }
    if (rows.length === 0) {
      setShowCodeBudgets(false);
      return;
    }
    setIsSavingCodeBudgets(true);
    try {
      await MatterCostCodeBudgetService.saveBudgets(matterId, rows);
      // Apply locally and re-derive the overall budget (matters.budget = Σ code budgets, which the
      // server just synced) so the total bar + the matter-total guard track the new total this session.
      const merged: CostCodeBudgetMap = { ...costCodeBudgets };
      for (const r of rows) {
        merged[r.cost_code] = { budget: r.budget_hours, group: costCodeBudgets[r.cost_code]?.group || 0 };
      }
      const newSum = Object.values(merged).reduce((s, b) => s + (b.budget || 0), 0);
      setCostCodeBudgets(merged);
      setMatterBudgetHours(newSum > 0 ? newSum : null);
      setShowCodeBudgets(false);
      toastService.success("Cost code budgets saved");
    } catch (e) {
      console.error("Error saving cost code budgets:", e);
      toastService.error(budgetErrorMessage(e, "cost code budgets"));
    } finally {
      setIsSavingCodeBudgets(false);
    }
  };

  const budgetPercent =
    overallDenominator != null && overallDenominator > 0
      ? Math.round((projectedTotalProjectHours / overallDenominator) * 100)
      : 0;
  const isOverBudget = overallDenominator != null && overallDenominator > 0
    ? projectedTotalProjectHours > overallDenominator
    : false;
  const showTotalProgress = overallDenominator != null && overallDenominator > 0;
  // Cap bar fill at 100% so the bar never extends past the track
  const barFillPercent = Math.min(100, budgetPercent);
  // The Log Time add-guard mirrors the SERVER matter-total wall (matters.budget, now the derived
  // sum of the code budgets). Use the derived total (overallDenominator), not the raw stored
  // matters.budget, so a lagging stored value can't disable Log Time while codes still have room.
  const atOrOverBudget =
    overallDenominator != null && overallDenominator > 0 && projectedTotalProjectHours >= overallDenominator;

  // Legend popover (refined grid, as on the firm-wide timesheet): replaces the toolbar sentence.
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const legendButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeLegend = useCallback(() => setIsLegendOpen(false), []);

  return (
    <div className="bts">
      {isLockedForUser && (
        <div className="bts-locked-banner" style={{ background: '#fff3cd', color: '#856404', padding: '10px 16px', borderRadius: '6px', marginBottom: '12px', fontSize: '14px', fontWeight: 500 }}>
          This project is {matterStatus?.toLowerCase().includes('complete') ? 'complete' : 'on hold'}. Time entry is locked.
        </div>
      )}
      <div className="bts-toolbar">
        <div className="bts-toolbar-left">
          <div className="bts-nav">
            <div className="bts-week-stepper">
              <button type="button" className="bts-nav-btn" onClick={handlePrevWeek} aria-label="Previous week">
                <FiChevronLeft size={16} aria-hidden="true" />
              </button>
              <span className="bts-week-label">
                {format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}
              </span>
              <button type="button" className="bts-nav-btn" onClick={handleNextWeek} aria-label="Next week">
                <FiChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
            <button type="button" className="bts-today-btn" onClick={handleToday}>
              Today
            </button>
          </div>
          {!isTimesheetOnlyUser && (
            <div className="bts-view-toggle">
              <button
                type="button"
                className={`bts-view-btn ${timesheetViewMode === "my" ? "active" : ""}`}
                aria-pressed={timesheetViewMode === "my"}
                onClick={() => setTimesheetViewMode("my")}
              >
                <span>My View</span>
              </button>
              <button
                type="button"
                className={`bts-view-btn ${timesheetViewMode === "all" ? "active" : ""}`}
                aria-pressed={timesheetViewMode === "all"}
                onClick={() => setTimesheetViewMode("all")}
              >
                <span>All View</span>
              </button>
            </div>
          )}
        </div>
        <div className="bts-toolbar-right">
          <button
            type="button"
            ref={legendButtonRef}
            className="bts-icon-btn"
            onClick={() => setIsLegendOpen((o) => !o)}
            aria-label="Legend"
            aria-haspopup="dialog"
            aria-expanded={isLegendOpen}
          >
            <FiHelpCircle size={17} aria-hidden="true" />
          </button>
          {isDayLockAdmin && (
            <button
              type="button"
              className="tdl-requests-btn"
              onClick={() => setIsRequestsPanelOpen(true)}
              aria-label={`Requests (${pendingRequests.requests.length})`}
            >
              <FiInbox size={15} aria-hidden="true" />
              <span>Requests</span>
              {pendingRequests.requests.length > 0 && (
                <span className="tdl-requests-badge">{pendingRequests.requests.length}</span>
              )}
            </button>
          )}
          {isDayLockAdmin && (
            <button
              type="button"
              className="tdl-requests-btn"
              onClick={() => setIsLockedDaysPanelOpen(true)}
              disabled={!dayLocks.isReady}
            >
              <FiLock size={15} aria-hidden="true" />
              <span>Locked days</span>
            </button>
          )}
          <div className="bts-save-switch" role="group" aria-label="Save">
            <span className="bts-save-switch-label" aria-hidden="true">Save</span>
            <div className="bts-segmented">
              <button
                type="button"
                className={`bts-segment ${!isBatchMode ? "active" : ""}`}
                aria-pressed={!isBatchMode}
                onClick={() => { if (isBatchMode) handleDiscardPending(); }}
                disabled={isBatchMode && (pendingEdits.size > 0 || editingCell !== null || isSaving)}
                title={isBatchMode && (pendingEdits.size > 0 || editingCell !== null || isSaving) ? "Save All or Discard first" : undefined}
              >
                Instantly
              </button>
              <button
                type="button"
                className={`bts-segment ${isBatchMode ? "active" : ""}`}
                aria-pressed={isBatchMode}
                onClick={() => setIsBatchMode(true)}
              >
                In batch
              </button>
            </div>
            {isBatchMode && (
              <div className="bts-batch-actions" role="group" aria-label="Batch edits">
                {/* The count is announced, not shown: Save All sits beside the switch on its own. */}
                <span className="bts-pending-count bts-sr-only" role="status">
                  {pendingEdits.size} pending {pendingEdits.size === 1 ? "edit" : "edits"}
                </span>
                <button
                  type="button"
                  className="bts-discard-btn"
                  onClick={handleDiscardPending}
                  disabled={isSaving}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="bts-save-all-btn"
                  onClick={handleSaveAllPending}
                  disabled={pendingEdits.size === 0 || isSaving}
                >
                  {isSaving ? "Saving..." : "Save All"}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`bts-add-btn ${(atOrOverBudget || isLockedForUser) ? "bts-add-btn--disabled" : ""}`}
            onClick={handleAddEntry}
            disabled={atOrOverBudget || isLockedForUser}
            title={isLockedForUser ? "This project is locked (complete or on hold)." : atOrOverBudget ? "Total amount of billing is completed. Cannot go over limit." : undefined}
          >
            <FontAwesomeIcon icon={faPlus} /> Log Time
          </button>
        </div>
      </div>
      <AnchoredPopover
        anchor={legendButtonRef.current}
        open={isLegendOpen}
        onClose={closeLegend}
        width={300}
        ariaLabel="Legend"
        className="bts-legend-popover"
      >
        <ul className="bts-legend-list">
          <li>
            <span className="bts-legend-swatch is-invoiced" aria-hidden="true">
              <FontAwesomeIcon icon={faCircleCheck} className="bts-legend-check" />
            </span>
            This check mark means the item has already been invoiced
          </li>
          <li><span className="bts-legend-swatch is-pending" aria-hidden="true" />Unsaved batch change</li>
          <li><span className="bts-legend-swatch is-over" aria-hidden="true" />Cost code at or over budget</li>
          <li>
            <span className="bts-legend-swatch is-day-locked" aria-hidden="true">
              <FontAwesomeIcon icon={faLock} />
            </span>
            Day locked
          </li>
          <li>
            <span className="bts-legend-swatch is-request-pending" aria-hidden="true">
              <FontAwesomeIcon icon={faClock} />
            </span>
            Time request pending
          </li>
          <li><span className="bts-legend-swatch is-request-declined" aria-hidden="true">✕</span>Time request declined</li>
        </ul>
      </AnchoredPopover>

      {(showTotalProgress || (!isTimesheetOnlyUser && !isLoading && displayedRows.length > 0)) && (
        <div className="bts-strip">
          {showTotalProgress && (
            <div className="bts-progress">
              <span className="bts-progress-label">Total project progress</span>
              <span className={`bts-progress-value ${isOverBudget ? "over" : ""}`}>
                {formatHoursToHoursMinutes(projectedTotalProjectHours)} /{" "}
                {formatHoursToHoursMinutes(overallDenominator!)}
                <span className={`bts-pct ${isOverBudget ? "over" : ""}`}>
                  {budgetPercent}%
                </span>
                {pendingEdits.size > 0 && (
                  <span className="bts-progress-unsaved">Includes unsaved changes</span>
                )}
              </span>
              <div className="bts-progress-track">
                <div
                  className={`bts-progress-fill ${isOverBudget ? "over" : ""}`}
                  style={{ width: `${barFillPercent}%` }}
                />
              </div>
            </div>
          )}
          {!isTimesheetOnlyUser && !isLoading && displayedRows.length > 0 && (
            <div className="bts-strip-amounts">
              <span className="bts-amount-tag bts-amount-billed">
                Billed: ${displayedRows.reduce((s, r) => s + r.amounts.billedTotal, 0).toFixed(2)}
              </span>
              <span className="bts-amount-tag bts-amount-costofwork">
                CoW: ${displayedRows.reduce((s, r) => s + r.amounts.costofworkTotal, 0).toFixed(2)}
              </span>
              <span className="bts-amount-tag bts-amount-fieldrate">
                Field: ${displayedRows.reduce((s, r) => s + r.amounts.fieldrateTotal, 0).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {canEditBudget && budgetEditableCodes.length > 0 && (
        <div className="bts-code-budgets">
          <button
            type="button"
            className="bts-code-budgets-toggle"
            onClick={() => (showCodeBudgets ? setShowCodeBudgets(false) : openCodeBudgets())}
            aria-expanded={showCodeBudgets}
          >
            <FontAwesomeIcon icon={showCodeBudgets ? faChevronUp : faChevronDown} />
            <span>Cost code budgets</span>
            {hasCodeBudgets && (
              <span className="bts-code-budgets-sum" title="Sum of per-cost-code budgets">
                Σ {formatHoursToHoursMinutes(sumCodeBudgets)}
              </span>
            )}
          </button>
          {showCodeBudgets && (
            <div className="bts-code-budgets-panel">
              <table className="bts-code-budgets-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Spent</th>
                    <th>Budget (hrs)</th>
                    <th>
                      <span className="bts-cbp-th-label">
                        %
                        <span className="bts-cbp-info" tabIndex={0} role="button" aria-label="What the percentage means">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          <span className="bts-cbp-tooltip" role="tooltip">
                            % shows how much of this cost code's budget has been used on the project.
                          </span>
                        </span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {budgetEditableCodes.map((c) => {
                    const group = projectedCodeGroup(c.code);
                    const draft = codeBudgetDrafts[c.code] ?? "";
                    const budget = draft.trim() === "" ? 0 : parseFloat(draft);
                    const validBudget = Number.isFinite(budget) && budget > 0;
                    const pct = validBudget ? Math.round((group / budget) * 100) : 0;
                    const over = validBudget && group > budget;
                    return (
                      <tr key={c.code}>
                        <td className="bts-cbp-code">{c.code}</td>
                        <td className="bts-cbp-desc" title={`${c.code} - ${c.description}`}>{c.description}</td>
                        <td className="bts-cbp-spent">{formatHoursToHoursMinutes(group)}</td>
                        <td className="bts-cbp-input">
                          <input
                            type="number"
                            step="1"
                            min="0"
                            className="bts-budget-input"
                            value={draft}
                            onChange={(e) =>
                              setCodeBudgetDrafts((prev) => ({ ...prev, [c.code]: e.target.value }))
                            }
                            disabled={isSavingCodeBudgets}
                            aria-label={`Budget hours for cost code ${c.code}`}
                          />
                        </td>
                        <td className={`bts-cbp-pct ${over ? "over" : ""}`}>{validBudget ? `${pct}%` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="bts-code-budgets-actions">
                <button
                  type="button"
                  className="bts-budget-save bts-code-budgets-save"
                  onClick={handleSaveAllCodeBudgets}
                  disabled={isSavingCodeBudgets}
                >
                  {isSavingCodeBudgets ? "Saving…" : "Save budgets"}
                </button>
                <button
                  type="button"
                  className="bts-budget-cancel bts-code-budgets-cancel"
                  onClick={() => setShowCodeBudgets(false)}
                  disabled={isSavingCodeBudgets}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bts-grid" ref={gridRef}>
        {isLoading ? (
          <div className="bts-loading">
            <div className="bts-spinner" />
            <span>Loading timesheet…</span>
          </div>
        ) : (
          <table className="bts-table">
            <thead>
              <tr>
                <th className="bts-th-emp">Employee / Category</th>
                {weekDates.map((d, colIndex) => (
                  <th
                    key={d.toISOString()}
                    className={`bts-th-day ${isToday(d) ? "is-today" : ""} ${hoveredCell?.colIndex === colIndex ? "bts-col-highlighted" : ""} ${(isDayLockAdmin || dayLockAppliesToMe) && dayLocks.isDayLocked(format(d, "yyyy-MM-dd")) ? "tdl-header-locked" : ""}`}
                  >
                    <span className="bts-day-name">{format(d, "EEE")}</span>
                    <span className={`bts-day-num ${isToday(d) ? "today-circle" : ""}`}>
                      {format(d, "d")}
                    </span>
                    {isDayLockAdmin && (
                      <DayLockHeaderToggle
                        weekday={d.getDay()}
                        locked={dayLocks.isWeekdayLocked(d.getDay())}
                        disabled={!dayLocks.isReady}
                        onChange={(lock) => toggleWeekdayLock(d.getDay(), lock)}
                        dateKey={format(d, "yyyy-MM-dd")}
                        dateLocks={dayLocks.dateLocksFor(format(d, "yyyy-MM-dd"))}
                        onUnlockDate={unlockDate}
                        variant="chip"
                      />
                    )}
                    {dayLockAppliesToMe && dayLocks.isDayLocked(format(d, "yyyy-MM-dd")) && (
                      <span className="tdl-header-chip">
                        <FontAwesomeIcon
                          icon={faLock}
                          className="tdl-header-lock-icon"
                          title={dayLocks.lockLabelFor(format(d, "yyyy-MM-dd")) ?? "Day locked"}
                        />
                        Day locked
                      </span>
                    )}
                  </th>
                ))}
                <th className="bts-th-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.length === 0 ? (
                <tr>
                  <td colSpan={weekDates.length + 2} className="bts-empty-td">
                    <div className="bts-empty">
                      <FontAwesomeIcon icon={faClock} className="bts-empty-icon" />
                      <p className="bts-empty-title">
                        {timesheetViewMode === "my" ? "No time entries found" : "No employees assigned"}
                      </p>
                      <p className="bts-empty-sub">
                        {timesheetViewMode === "my" 
                          ? "Click Log Time to add your first time entry for this week."
                          : "Assign employees to this project or click Log Time to add entries."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayedRows.map((row) => {
                  const isMe = row.employeeId === activeEmployeeId;
                  const isExpanded = expandedEmployees.has(row.employeeId);

                  return (
                    <React.Fragment key={row.employeeId}>
                      <tr
                        className={`bts-emp-row ${isMe ? "bts-row-me" : ""} ${
                          isMe && isExpanded ? "bts-emp-row--sticky" : ""
                        }`}
                        onClick={() => toggleEmployee(row.employeeId)}
                      >
                        <td className="bts-td-emp">
                          <div className="bts-emp-info">
                            <button type="button" className="bts-expand-btn" aria-label={isExpanded ? "Collapse" : "Expand"}>
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            </button>
                            <span className="bts-avatar">
                              {row.employeeName.charAt(0).toUpperCase()}
                            </span>
                            <div className="bts-emp-text">
                              <div className="bts-emp-name-line">
                                <span className="bts-emp-name">{row.employeeName}</span>
                                {isMe && <span className="bts-you-tag">You</span>}
                              </div>
                              <span className="bts-emp-sub">
                                {formatHoursToHoursMinutes(row.rowTotal)} this week
                              </span>
                              {!isTimesheetOnlyUser && (
                                <div className="bts-emp-amounts">
                                  <span className="bts-amount-tag bts-amount-billed" title="Billed Amount">
                                    Billed: ${row.amounts.billedTotal.toFixed(2)}
                                  </span>
                                  <span className="bts-amount-tag bts-amount-costofwork" title="Cost of Work">
                                    CoW: ${row.amounts.costofworkTotal.toFixed(2)}
                                  </span>
                                  <span className="bts-amount-tag bts-amount-fieldrate" title="Field Rate">
                                    Field: ${row.amounts.fieldrateTotal.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {weekDates.map((d) => {
                          const key = format(d, "yyyy-MM-dd");
                          const hours = row.days[key] || 0;
                          const da = row.dayAmounts[key];
                          const hasAmounts = da && (da.billed > 0 || da.costofwork > 0 || da.fieldrate > 0);
                          return (
                            <td
                              key={key}
                              className={`bts-td-day ${isToday(d) ? "today-col" : ""}`}
                            >
                              <span className={hours > 0 ? "bts-cell-hrs" : ""}>
                                {hours > 0 ? formatHoursToDisplay(hours) : ""}
                              </span>
                              {!isTimesheetOnlyUser && hasAmounts && (
                                <div className="bts-cell-amounts">
                                  {da.billed > 0 && (
                                    <span className="bts-cell-amt bts-amt-billed">${da.billed.toFixed(0)}</span>
                                  )}
                                  {da.costofwork > 0 && (
                                    <span className="bts-cell-amt bts-amt-costofwork">${da.costofwork.toFixed(0)}</span>
                                  )}
                                  {da.fieldrate > 0 && (
                                    <span className="bts-cell-amt bts-amt-fieldrate">${da.fieldrate.toFixed(0)}</span>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="bts-td-total">
                          {formatHoursToHoursMinutes(row.rowTotal)}
                        </td>
                      </tr>

                      {isExpanded &&
                        row.categories.map((cat) => {
                          const isEditable = canEditEmployeeTime(row.employeeId);
                          const catRowKey = `${row.employeeId}-${cat.categoryCode}`;
                          const isRowHighlighted = hoveredCell?.rowKey === catRowKey;
                          const cb = costCodeBudgets[cat.categoryCode];
                          const codeBudget = cb?.budget ?? 0;
                          const codeGroup = projectedCodeGroup(cat.categoryCode);
                          const codeHasBudget = codeBudget > 0;
                          const codePercent = codeHasBudget ? Math.round((codeGroup / codeBudget) * 100) : 0;
                          const codeOver = codeHasBudget && codeGroup > codeBudget;
                          const codeAtOrOver = codeHasBudget && codeGroup >= codeBudget;
                          return (
                            <tr
                              key={catRowKey}
                              className={`bts-cat-row ${isRowHighlighted ? "bts-row-highlighted" : ""}`}
                            >
                              <td className="bts-td-cat">
                                <div className="bts-cat-head">
                                  <span className="bts-cat-code">{cat.categoryCode}</span>
                                  <span
                                    className="bts-cat-desc"
                                    title={`${cat.categoryCode} - ${cat.categoryDescription}`}
                                  >
                                    {cat.categoryDescription}
                                  </span>
                                </div>
                                {codeHasBudget ? (
                                  <div className="bts-code-progress" title={`${formatHoursToHoursMinutes(codeGroup)} of ${formatHoursToHoursMinutes(codeBudget)} (all users)`}>
                                    <div className="bts-code-progress-track">
                                      <div
                                        className={`bts-code-progress-fill ${codeOver ? "over" : ""}`}
                                        style={{ width: `${Math.min(100, codePercent)}%` }}
                                      />
                                    </div>
                                    <span className={`bts-code-progress-label ${codeOver ? "over" : ""}`}>
                                      {formatHoursToHoursMinutes(codeGroup)}/{formatHoursToHoursMinutes(codeBudget)} · {codePercent}%
                                    </span>
                                  </div>
                                ) : null}
                              </td>
                              {weekDates.map((d, colIndex) => {
                                const key = format(d, "yyyy-MM-dd");
                                const dayData = cat.days[key];
                                const hours = dayData?.hours || 0;
                                const cellInvoiced = dayData?.invoiced || false;
                                const todayCol = isToday(d);
                                const isEditing =
                                  editingCell?.employeeId === row.employeeId &&
                                  editingCell?.categoryCode === cat.categoryCode &&
                                  editingCell?.dateKey === key;
                                const canEdit = isEditable && !editingCell;
                                const isColHighlighted = hoveredCell?.colIndex === colIndex;
                                const isCellHighlighted = isRowHighlighted && isColHighlighted;
                                const hasPending = pendingEdits.has(makeBtsPendingKey(row.employeeId, cat.categoryCode, key));
                                // Day lock (F2): only the permish 2/3 user's own editable cells.
                                const dayLockedCell = dayLockAppliesToMe && isEditable && !!matterId && dayLocks.isDayLocked(key);
                                const cellRequest = dayLockedCell
                                  ? weekRequests.get(requestCellKey(row.employeeId, String(matterId), cat.categoryCode, key))
                                  : undefined;
                                const dayLockClass = !dayLockedCell
                                  ? ""
                                  : cellRequest?.status === "pending"
                                  ? "day-locked request-pending"
                                  : cellRequest?.status === "declined"
                                  ? "day-locked request-declined"
                                  : "day-locked";
                                const dayLockTitle = !dayLockedCell
                                  ? null
                                  : cellRequest?.status === "pending"
                                  ? `Day locked — request pending: ${formatRequestHours(cellRequest.currentHours)} → ${formatRequestHours(cellRequest.requestedHours)}`
                                  : cellRequest?.status === "declined"
                                  ? `Day locked — request declined${cellRequest.decisionNote ? `: ${cellRequest.decisionNote}` : ""}`
                                  : "Day locked — click to request time";

                                return (
                                  <td
                                    key={key}
                                    className={`bts-td-day bts-cat-day ${
                                      todayCol ? "today-col" : ""
                                    } ${cellInvoiced ? "invoiced" : hasPending ? "pending-edit" : canEdit ? "editable" : ""} ${
                                      isEditing ? "editing" : ""
                                    } ${codeAtOrOver ? "bts-over-cap" : ""} ${isColHighlighted ? "bts-col-highlighted" : ""} ${isCellHighlighted ? "bts-cell-highlighted" : ""} ${dayLockClass}`}
                                    onMouseEnter={() => setHoveredCell({ rowKey: catRowKey, colIndex })}
                                    onMouseLeave={() => setHoveredCell(null)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      lastClickedCellRef.current = e.currentTarget;
                                      if (canEdit) {
                                        handleCellClick(
                                          row.employeeId,
                                          cat.categoryCode,
                                          key,
                                          hours,
                                          cellInvoiced
                                        );
                                      }
                                    }}
                                    title={
                                      cellInvoiced ? "Already invoiced" : (dayLockTitle ?? (dayData?.notes?.join("\n") || "Click to add time"))
                                    }
                                  >
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.25"
                                        min="0"
                                        max="24"
                                        className="bts-input"
                                        ref={blockNumberWheel}
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={handleCellSave}
                                        onKeyDown={handleCellKeyDown}
                                        autoFocus
                                        disabled={isSaving}
                                        placeholder="hrs"
                                      />
                                    ) : hours > 0 ? (
                                      <>
                                        <span className="bts-cell-hrs-wrap">
                                          <span className="bts-cat-hrs">
                                            {formatHoursToDisplay(hours)}
                                          </span>
                                          {cellInvoiced && (
                                            <FontAwesomeIcon
                                              icon={faCircleCheck}
                                              className="bts-invoiced-check"
                                              title="Already invoiced"
                                            />
                                          )}
                                        </span>
                                        {!isTimesheetOnlyUser && (() => {
                                          const b = dayData?.billed || 0;
                                          const c = dayData?.costofwork || 0;
                                          const f = dayData?.fieldrate || 0;
                                          if (b <= 0 && c <= 0 && f <= 0) return null;
                                          return (
                                            <div className="bts-cell-amounts">
                                              {b > 0 && <span className="bts-cell-amt bts-amt-billed">${b.toFixed(0)}</span>}
                                              {c > 0 && <span className="bts-cell-amt bts-amt-costofwork">${c.toFixed(0)}</span>}
                                              {f > 0 && <span className="bts-cell-amt bts-amt-fieldrate">${f.toFixed(0)}</span>}
                                            </div>
                                          );
                                        })()}
                                      </>
                                    ) : (
                                      <span className="bts-cell-placeholder">
                                        {canEdit && (
                                          <span className="bts-plus">+</span>
                                        )}
                                      </span>
                                    )}
                                    {!isEditing && dayLockedCell && (cellRequest?.status === "pending" ? (
                                      <span className="tdl-cell-request">
                                        <FontAwesomeIcon icon={faClock} /> {formatRequestHours(cellRequest.currentHours)} → {formatRequestHours(cellRequest.requestedHours)}
                                      </span>
                                    ) : (
                                      <FontAwesomeIcon icon={faLock} className="tdl-cell-lock" aria-label="Day locked" />
                                    ))}
                                  </td>
                                );
                              })}
                              <td className="bts-td-total bts-cat-total">
                                {cat.rowTotal > 0
                                  ? formatHoursToDisplay(cat.rowTotal)
                                  : ""}
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              )}
              {displayedRows.length > 0 && (
                <>
                  <tr className="bts-footer">
                    <td className="bts-footer-label">Daily Total</td>
                    {weekDates.map((d) => {
                      const key = format(d, "yyyy-MM-dd");
                      return (
                        <td
                          key={key}
                          className={`bts-footer-day ${isToday(d) ? "today-col" : ""}`}
                        >
                          {formatHoursToHoursMinutes(displayedDailyTotals[key] || 0)}
                        </td>
                      );
                    })}
                    <td className="bts-footer-total">
                      <span className="bts-week-total">{formatHoursToHoursMinutes(displayedWeekTotal)}</span>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        )}
      </div>

      <LogBillableEntryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleModalSuccess}
        matterId={matterId}
      />
      {dayLockAppliesToMe && (
        <TimeRequestPopover
          anchor={requestAnchor}
          open={requestCell !== null}
          onClose={closeTimeRequest}
          cell={requestCell}
          existing={
            requestCell
              ? weekRequests.get(requestCellKey(requestCell.employeeId, requestCell.matterId, requestCell.costCode, requestCell.dateKey))
              : null
          }
          onChanged={() => void invalidateTimeRequests()}
          onStale={dayLocks.refresh}
        />
      )}
      {isDayLockAdmin && (
        <TimeRequestsPanel
          open={isRequestsPanelOpen}
          onClose={() => setIsRequestsPanelOpen(false)}
          requests={pendingRequests.requests}
          onApproved={handleRequestApproved}
          onChanged={() => void invalidateTimeRequests()}
          onJumpToRequest={showRequestInGrid}
          onStale={dayLocks.refresh}
        />
      )}
      {isDayLockAdmin && (
        <LockedDaysPanel
          open={isLockedDaysPanelOpen}
          onClose={() => setIsLockedDaysPanelOpen(false)}
          lockedWeekdays={dayLocks.lockedWeekdays}
          lockedDates={dayLocks.lockedDates}
          onUnlockWeekday={(weekday) => toggleWeekdayLock(weekday, false)}
          onDeleteDateLock={unlockDate}
          onApplyPreset={dayLocks.applyPreset}
          onAddDateLock={lockDate}
        />
      )}
    </div>
  );
};

export default BillingTimesheetView;
